import React, { useState, useEffect } from "react";
import { Button, Spinner } from "@material-tailwind/react";
import axiosInstance from "../../../services/api/axios";
import { Download } from "lucide-react";
import { toast } from "react-toastify";
import { FaFilePdf } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import { IconButton, PrimaryButton } from "../../../components/Buttons";
import usePrivilege from "../../../hooks/usePrivilege";
import { getMediaUrl, mapMediaItems } from "../../../utils/media";
const Receipts = ({
  formData,
  handleInputChange,
  setFormData,
  isHandover,
  isCancelled,
}) => {
  const [selectedTab, setSelectedTab] = useState("token");
  const [selectedFile, setSelectedFile] = useState(null);
  const [tokenReceipts, setTokenReceipts] = useState([]);
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const [demandLetters, setDemandLetters] = useState([]);
  const [cancellationDocs, setCancellationDocs] = useState([]);
  const [handoverDocsSigned, setHandoverDocsSigned] = useState([]);
  const [handoverDocsUnsigned, setHandoverDocsUnsigned] = useState([]);
  const [occupancyDocs, setOccupancyDocs] = useState([]);
  const [costSheetDocs, setCostSheetDocs] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [bookingLetters, setBookingLetters] = useState([]);
  const [bookingLetterFile, setBookingLetterFile] = useState(null);
  const [handoverLetterFile, setHandoverLetterFile] = useState(null);
  const [cancellationLetterFile, setCancellationLetterFile] = useState(null);
  const [cancellationDocsSigned, setCancellationDocsSigned] = useState([]);
  const [cancellationDocsUnsigned, setCancellationDocsUnsigned] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingHandoverLetter, setUploadingHandoverLetter] = useState(false);
  const { hasPrivilege } = usePrivilege();
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setSelectedFile(null);
    setPdfUrl(null);
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
    setPdfUrl(getMediaUrl(file));
  };
  const downloadFile = async () => {
    const downloadUrl = getMediaUrl(selectedFile);
    if (!downloadUrl) {
      return;
    }

    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        selectedFile?.name ||
        selectedFile?.key?.split("/").pop() ||
        "document.pdf";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
      if (downloadUrl) {
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
      }
    }
  };

  const handleBookingLetterUpload = (event) => {
    setBookingLetterFile(event.target.files[0]);
  };
  const handleCancellationLetterUpload = (event) => {
    setCancellationLetterFile(event.target.files[0]);
  };

  const handleHandoverLetterUpload = (event) => {
    setHandoverLetterFile(event.target.files[0]);
  };

  const uploadBookingLetter = async () => {
    if (!bookingLetterFile) {
      console.error("No booking letter file selected.");
      return;
    }
    const uploadFormData = new FormData();
 
    uploadFormData.append("customer_sales_id", formData.customerSalesId);
    uploadFormData.append("medium", bookingLetterFile);
  
    try {
      await axiosInstance.post(
        "/customer/sales/handover-letters/signed",
        uploadFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("Booking letter uploaded successfully!");
      setBookingLetterFile(null);
      // fetch data again to show the updated list
      fetchBookingLetters();
    } catch (error) {
      console.error("Error uploading booking letter:", error);
      alert("Error uploading booking letter.");
    }
  };
  const uploadCancellationLetter = async () => {
    if (!cancellationLetterFile) {
      console.error("No cancellation letter file selected.");
      return;
    }
    const uploadFormData = new FormData();
    uploadFormData.append("customer_sales_id", formData.customerSalesId);
    uploadFormData.append("medium", cancellationLetterFile);
    try {
      await axiosInstance.post(
        "/customer/sales/cancellation-letters/signed",
        uploadFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("Cancellation letter uploaded successfully!");
      setCancellationLetterFile(null);
      fetchCancellationDocs();
      // fetch data again to show the updated list
    } catch (error) {
      console.error("Error uploading cancellation letter:", error);
      alert("Error uploading cancellation letter.");
    }
  };

  const uploadHandoverLetter = async () => {
    if (!handoverLetterFile) {
      console.error("No handover letter file selected.");
      return;
    }
    setUploadingHandoverLetter(true);
    const uploadFormData = new FormData();
    uploadFormData.append("customer_sales_id", formData.customerSalesId);
    uploadFormData.append("medium", handoverLetterFile);
    try {
      await axiosInstance.post(
        "/customer/sales/handover-letters/signed",
        uploadFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("Handover letter uploaded successfully!");
      setHandoverLetterFile(null);
      fetchHandoverDocs();
      fetchCancellationDocs();
    } catch (error) {
      console.error("Error uploading handover letter:", error);
      if (error.response && error.response.data && error.response.data.detail) {
        toast.error(
          error.response.data.detail || "Error uploading handover letter."
        );
      } else {
        toast.error("Error uploading handover letter.");
      }
    } finally {
      setUploadingHandoverLetter(false);
    }
  };

  const fetchReceipts = async (isToken) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/customer/sales/receipts", {
        params: {
          customer_sales_id: formData.customerSalesId,
          isToken: isToken,
        },
      });
    

      const formattedFiles = mapMediaItems(
        response,
        isToken ? "Token Receipt" : "Payment Receipt"
      );
      if (isToken) {
        setTokenReceipts(formattedFiles);
      } else {
        setPaymentReceipts(formattedFiles);
      }
    } catch (error) {
      console.error("Error fetching receipts:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchCancellationDocs = async () => {
    setLoading(true);
    try {
      const signedResponse = await axiosInstance.get(
        "/customer/sales/cancellation-letters",
        {
          params: {
            customer_sales_id: formData.customerSalesId,
            isSigned: true,
          },
        }
      );
      const unsignedResponse = await axiosInstance.get(
        "/customer/sales/cancellation-letters",
        {
          params: {
            customer_sales_id: formData.customerSalesId,
            isSigned: false,
          },
        }
      );
     
      setCancellationDocsSigned(
        mapMediaItems(signedResponse, "Cancellation Signed")
      );
      setCancellationDocsUnsigned(
        mapMediaItems(unsignedResponse, "Cancellation Unsigned")
      );
    } catch (error) {
    
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingLetters = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        "/customer/sales/booking-letters",
        {
          params: {
            customer_sales_id: formData.customerSalesId,
          },
        }
      );
     
      setBookingLetters(mapMediaItems(response, "Booking Letter"));
    } catch (error) {
      console.error("Error fetching booking documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHandoverDocs = async () => {
    setLoading(true);
    try {
      const signedResponse = await axiosInstance.get(
        "/customer/sales/handover-letters",
        {
          params: {
            customer_sales_id: formData.customerSalesId,
            isSigned: true,
          },
        }
      );
      const unsignedResponse = await axiosInstance.get(
        "/customer/sales/handover-letters",
        {
          params: {
            customer_sales_id: formData.customerSalesId,
            isSigned: false,
          },
        }
      );
      
      setHandoverDocsSigned(
        mapMediaItems(signedResponse, "Handover Signed")
      );
      setHandoverDocsUnsigned(
        mapMediaItems(unsignedResponse, "Handover Unsigned")
      );
    } catch (error) {
   
      
    } finally {
      setLoading(false);
    }
  };

  const fetchCostSheetDocs = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/customer/sales/cost-sheet", {
        params: {
          customer_sales_id: formData.customerSalesId,
        },
      });
      setCostSheetDocs(mapMediaItems(response, "Cost Sheet"));
    } catch (error) {
      console.error("Error fetching cost sheet documents:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.customerSalesId) {
      fetchReceipts(true);
      fetchReceipts(false);
      fetchCancellationDocs();
      fetchBookingLetters();
      fetchCostSheetDocs();
      fetchHandoverDocs();
    }
  }, [formData.customerSalesId]);

  return (
    <div className="flex">

      <div className="w-1/6 p-4">
        <div className="mb-4">
          <button
            onClick={() => handleTabChange("token")}
            className={`w-full p-2 rounded border font-normal ${selectedTab === "token" ? "bg-gray-200" : "hover:bg-gray-100"
              }`}
          >
            Token Receipts
          </button>
          <button
            onClick={() => handleTabChange("payment")}
            className={`w-full p-2 rounded border mt-2 font-normal ${selectedTab === "payment" ? "bg-gray-200" : "hover:bg-gray-100"
              }`}
          >
            Payment Receipts
          </button>
          <button
            onClick={() => handleTabChange("demand")}
            className={`w-full p-2 rounded border mt-2 font-normal ${selectedTab === "demand" ? "bg-gray-200" : "hover:bg-gray-100"
              }`}
          >
            Demand Letters
          </button>
          <button
            onClick={() => handleTabChange("booking")}
            className={`w-full p-2 rounded border mt-2 font-normal ${selectedTab === "booking" ? "bg-gray-200" : "hover:bg-gray-100"
              }`}
          >
            Booking Letter
          </button>
          {/* <button
            onClick={() => handleTabChange("cancellation")}
            className={`w-full p-2 rounded border mt-2 font-normal ${
              selectedTab === "cancellation"
                ? "bg-gray-200"
                : "hover:bg-gray-100"
            }`}
          >
            Cancellation
          </button> */}
          <button
            onClick={() => handleTabChange("handover")}
            className={`w-full p-2 rounded border mt-2 font-normal ${selectedTab === "handover" ? "bg-gray-200" : "hover:bg-gray-100"
              }`}
          >
            Handover
          </button>

          <button
            onClick={() => handleTabChange("costSheet")}
            className={`w-full p-2 rounded border mt-2 font-normal ${selectedTab === "costSheet" ? "bg-gray-200" : "hover:bg-gray-100"
              }`}
          >
            Cost Sheet
          </button>
        </div>
      </div>
      <div className="flex-1 p-4 py-8 border-l">
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div role="status">
              <svg
                aria-hidden="true"
                class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {selectedTab === "token" && (
              <div className="space-y-2">
                <h2 className="  font-medium">Token Receipts</h2>
                {tokenReceipts?.map((file) => (
                  <div
                    key={file.key}
                    onClick={() => handleFileClick(file)}
                    className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaFilePdf className="text-flamingo-500 text-xl" />
                    {file.key}
                  </div>
                ))}
              </div>
            )}
            {selectedTab === "payment" && (
              <div className="space-y-2">
                <h3 className="  font-medium">Payment Receipts</h3>
                {paymentReceipts?.map((file) => (
                  <div
                    key={file.key}
                    onClick={() => handleFileClick(file)}
                    className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaFilePdf className="text-flamingo-500 text-xl" />
                    {file.key}
                  </div>
                ))}
              </div>
            )}
            {selectedTab === "demand" && (
              <div className="space-y-2">
                <h3 className=" font-medium ">Demand Letters</h3>
                {demandLetters?.map((file) => (
                  <div
                    key={file.key}
                    onClick={() => handleFileClick(file)}
                    className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaFilePdf className="text-flamingo-500 text-xl" />
                    {file.key}
                  </div>
                ))}
              </div>
            )}
            {selectedTab === "booking" && (
              <div className="space-y-2">
                <div>
                  <h4 className=" font-medium ">Booking Letter</h4>
                  {bookingLetters?.map((file) => (
                    <div
                      key={file.key}
                      onClick={() => handleFileClick(file)}
                      className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                    >
                      <FaFilePdf className="text-flamingo-500 text-xl" />
                      {file.key}
                    </div>
                  ))}
                </div>
                {hasPrivilege("canEdit") && (
                <div className="mb-4 mt-4">
                  <h4 className="font-normal mb-2 text-sm">
                    Booking letter upload
                  </h4>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleBookingLetterUpload}
                      className="border rounded p-2 w-full"
                      disabled={isHandover || isCancelled}
                    />
                    <PrimaryButton
                      onClick={uploadBookingLetter}
                      disabled={!bookingLetterFile || isHandover || isCancelled}
                      className={"w-32"}
                    >
                      Upload
                    </PrimaryButton>
                  </div>
                </div>
                )}
              </div>
            )}

            {selectedTab === "cancellation" && (
              <div className="space-y-2">
                <h3 className="font-medium ">Cancellation Documents</h3>
                {hasPrivilege("canEdit") && (
                <div className="mb-4">
                  <h4 className="font-normal mb-2 text-sm">
                    Cancellation letter upload
                  </h4>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleCancellationLetterUpload}
                      className="border rounded p-2 w-full"
                      disabled={isHandover || isCancelled}
                    />
                    <PrimaryButton
                      onClick={uploadCancellationLetter}
                      disabled={
                        !cancellationLetterFile || isHandover || isCancelled
                      }
                      className={"w-32"}
                    >
                      Upload
                    </PrimaryButton>
                  </div>
                </div>
                )}
                <div>
                  <h5 className="font-normal mt-4 text-sm">Signed</h5>
                  <div className="space-y-2">
                    {cancellationDocsSigned?.map((file) => (
                      <div
                        key={file.key}
                        onClick={() => handleFileClick(file)}
                        className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                      >
                        <FaFilePdf className="text-flamingo-500 text-xl" />
                        {file.key}
                      </div>
                    ))}
                  </div>
                  <h5 className="font-normal mt-4 text-sm">Unsigned</h5>
                  <div className="space-y-2">
                    {cancellationDocsUnsigned?.map((file) => (
                      <div
                        key={file.key}
                        onClick={() => handleFileClick(file)}
                        className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                      >
                        <FaFilePdf className="text-flamingo-500 text-xl" />
                        {file.key}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {selectedTab === "handover" && (
              <div className="space-y-2">
                <h3 className="font-medium ">Handover Documents</h3>
                {hasPrivilege("canEdit") && (
                <div className="mb-4">
                  <h4 className="font-normal mb-2 text-sm">
                    Handover letter upload
                  </h4>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleHandoverLetterUpload}
                      className="border rounded p-2 w-full"
                      disabled={uploadingHandoverLetter}
                    />

                    <PrimaryButton
                      onClick={uploadHandoverLetter}
                      disabled={!handoverLetterFile || uploadingHandoverLetter}
                      className={"w-32"}
                    >
                      Upload
                    </PrimaryButton>
                  </div>
                </div>
                )}
                <div>
                  <h5 className="font-normal mt-4 text-sm">Signed</h5>
                  <div className="space-y-2">
                    {handoverDocsSigned?.map((file) => (
                      <div
                        key={file.key}
                        onClick={() => handleFileClick(file)}
                        className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                      >
                        <FaFilePdf className="text-flamingo-500 text-xl" />
                        {file.key}
                      </div>
                    ))}
                  </div>
                  <h5 className="font-normal mt-4 text-sm">Unsigned</h5>
                  <div className="space-y-2">
                    {handoverDocsUnsigned?.map((file) => (
                      <div
                        key={file.key}
                        onClick={() => handleFileClick(file)}
                        className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                      >
                        <FaFilePdf className="text-flamingo-500 text-xl" />
                        {file.key}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {selectedTab === "costSheet" && (
              <div className="space-y-2">
                <h3 className="font-medium">Cost Sheets</h3>
                {costSheetDocs?.map((file) => (
                  <div
                    key={file.key}
                    onClick={() => handleFileClick(file)}
                    className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaFilePdf className="text-flamingo-500 text-xl" />
                    {file.key}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <div className="w-1/3 p-4">
        {pdfUrl ? (
          <div className="bg-gray-50 p-4 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold">{selectedFile.key}</h4>
              {/* <Button
                onClick={downloadFile}
                className={"w-32"}
                icons={<Download className="size-4 mr-2" />}
                left
              >
                Download
              </Button> */}

              <Button
                size="sm"
                variant="outline"
                onClick={downloadFile}
                color="black"
                className="p-2 "
              >
                <Download size={17.5} />
              </Button>
            </div>
            <div className="border p-4 rounded mt-4">
              <iframe
                src={pdfUrl}
                title="PDF Preview"
                width="100%"
                height="500px"
                style={{ border: "none" }}
              />
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 p-4 rounded-lg shadow-md flex items-center justify-center h-96">
            <p>Select a document to preview</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Receipts;
